import { useMemo } from "react";
import { useCollectionKey } from "./useCollections";
import { useDashboardMetrics } from "./useContentService";
import type { DashboardMetric, DashboardMetricsData, DashboardMetricsGroupType } from "types/dashboardMetrics";
import type { QuadrantDataPoint, DirectQuadrantInput } from "screens/landing/tabs/collections/chartComponents/data/quadrantData";
import type { FairMarketChartData } from "screens/landing/tabs/collections/chartComponents/FairMarketChart/FairMarketChart";
import type { BuySellHoldChartItem } from "screens/landing/tabs/collections/chartComponents/BuySellHoldChart/BuySellHoldChart";

export interface CollectionDashboardData {
  collectionId?: string;
  collectionName?: string;
  ticker?: string;
  exchange?: string;
  metadataIds?: string[];
  dashboardMetrics?: DashboardMetricsData | DashboardMetric[];
}

export interface DashboardMetricsDataPoint {
  projectId?: string;
  projectName?: string;
  companyLogoUrl?: string;
  x?: number;
  y?: number;
  value?: number;
  color?: string;
  ticker?: string;
  currentPrice?: number;
  fairValueLow?: number;
  fairValueHigh?: number;
  rating?: number;
}

export const extractMetricsFromDashboardData = (
  dashboardMetrics: DashboardMetricsData | DashboardMetric[],
  groupType: DashboardMetricsGroupType
): DashboardMetric[] => {
  if (!Array.isArray(dashboardMetrics)) return [];

  if (dashboardMetrics.length > 0 && "group" in dashboardMetrics[0]) {
    const targetGroup = (dashboardMetrics as DashboardMetricsData).find((group) => group.group === groupType);
    return targetGroup?.metrics || [];
  } else {
    return dashboardMetrics as DashboardMetric[];
  }
};

export const processDashboardMetrics = (
  dashboardMetrics: DashboardMetric[],
  groupType: DashboardMetricsGroupType,
  collectionName?: string,
  collectionId?: string,
  ticker?: string
): DashboardMetricsDataPoint | null => {
  if (!dashboardMetrics || dashboardMetrics.length === 0) return null;
  switch (groupType) {
    case "pi_quadrant_index":
      return processQuadrantMetrics(dashboardMetrics, collectionName, collectionId);
    case "fmv":
      return processFmvMetrics(dashboardMetrics, collectionId, ticker);
    case "buy_sell":
      return processBuySellMetrics(dashboardMetrics, collectionId, ticker);
    case "scorecard":
      return processScorecardMetrics(dashboardMetrics, collectionName, collectionId);
    default:
      return null;
  }
};

const processQuadrantMetrics = (
  dashboardMetrics: DashboardMetric[],
  collectionName?: string,
  collectionId?: string
): DashboardMetricsDataPoint | null => {
  const fundamentalsMetric = dashboardMetrics.find((metric) => metric.subgroup === "fundamentals");
  const sentimentMetric = dashboardMetrics.find((metric) => metric.subgroup === "sentiment");

  if (!fundamentalsMetric || !sentimentMetric) return null;

  const x = (fundamentalsMetric.data?.[0] ?? 0) * 10;
  const y = (sentimentMetric.data?.[0] ?? 0) * 10;

  if (!fundamentalsMetric.data && !sentimentMetric.data) return null;

  return {
    projectId: collectionId || "",
    projectName: collectionName || "Project",
    x: Math.min(Math.max(x, 0), 100),
    y: Math.min(Math.max(y, 0), 100),
    color: "blue.500",
  };
};

const processFmvMetrics = (
  dashboardMetrics: DashboardMetric[],
  collectionId?: string,
  ticker?: string
): DashboardMetricsDataPoint | null => {
  const fmvMarket = dashboardMetrics.find((metric) => metric.subgroup === "market");
  const fmvFundamentals = dashboardMetrics.find((metric) => metric.subgroup === "fundamentals");

  if (!fmvMarket && !fmvFundamentals) return null;

  const currentPrice = fmvMarket?.data?.[0] ?? 0;
  const fairValueLow = fmvFundamentals?.data?.[0] ?? 0;
  const fairValueHigh = fmvFundamentals?.data?.[1] ?? 0;

  return {
    projectId: collectionId || "",
    ticker: ticker || "",
    currentPrice,
    fairValueLow,
    fairValueHigh,
  };
};

const processBuySellMetrics = (
  dashboardMetrics: DashboardMetric[],
  collectionId?: string,
  ticker?: string
): DashboardMetricsDataPoint | null => {
  const ratingMetric = dashboardMetrics.find((metric) => metric.subgroup === "buy_sell");

  if (!ratingMetric) return null;
  const rating = ratingMetric.data?.[0] ?? 0;

  return {
    projectId: collectionId || "",
    ticker,
    rating,
    color: "blue.500",
  };
};

const processScorecardMetrics = (
  dashboardMetrics: DashboardMetric[],
  collectionName?: string,
  collectionId?: string
): DashboardMetricsDataPoint | null => {
  const scoreMetric = dashboardMetrics.find((metric) => metric.subgroup === "score");

  if (!scoreMetric) return null;

  const value = scoreMetric.data?.[0] ?? 0;

  return {
    projectId: collectionId || "",
    projectName: collectionName || "Project",
    value,
    color: "blue.500",
  };
};

export const processCollectionData = (
  investmentScoreAnalytical?: { value: string },
  investmentScoreSentiment?: { value: string },
  name?: string,
  id?: string,
  companyLogoUrl?: string
): DashboardMetricsDataPoint | null => {
  const analyticalValue = investmentScoreAnalytical?.value ?? "";
  const sentimentValue = investmentScoreSentiment?.value ?? "";

  const regexInvestmentScoreAnalytical = analyticalValue.match(/\d+(\.\d+)?/g);
  const regexInvestmentScoreSentiment = sentimentValue.match(/\d+(\.\d+)?/g);

  const x = Number(regexInvestmentScoreAnalytical?.[0] ?? 0);
  const y = Number(regexInvestmentScoreSentiment?.[0] ?? 0);

  if (x === 0 && y === 0) return null;

  return {
    projectId: id || "",
    projectName: name || "",
    x: Math.min(Math.max(x, 0), 100),
    y: Math.min(Math.max(y, 0), 100),
    companyLogoUrl: companyLogoUrl,
    color: "gray.500",
  };
};

export const processDirectInput = (input: DirectQuadrantInput): DashboardMetricsDataPoint | null => {
  const { investmentScoreAnalytical, investmentScoreSentiment, projectName, id, companyLogoUrl } = input || {};
  return processCollectionData(investmentScoreAnalytical, investmentScoreSentiment, projectName, id, companyLogoUrl);
};

// Conversion functions to transform generic data to specific chart types
export const convertToQuadrantData = (data: DashboardMetricsDataPoint[]): QuadrantDataPoint[] => {
  return data
    .filter((item) => item.x !== undefined && item.y !== undefined)
    .map((item) => ({
      projectId: item.projectId,
      projectName: item.projectName || "",
      companyLogoUrl: item.companyLogoUrl || "",
      x: item.x!,
      y: item.y!,
      color: item.color || "gray.500",
    }));
};

export const convertToFairMarketData = (data: DashboardMetricsDataPoint[]): FairMarketChartData[] => {
  return data
    .filter((item) => item.ticker && item.currentPrice !== undefined)
    .map((item) => ({
      projectId: item.projectId,
      ticker: item.ticker!,
      currentPrice: item.currentPrice!,
      fairValueLow: item.fairValueLow,
      fairValueHigh: item.fairValueHigh,
    }));
};

export const convertToBuySellData = (data: DashboardMetricsDataPoint[]): { items: BuySellHoldChartItem[] } => {
  const items = data
    .filter((item) => item.ticker && item.rating !== undefined)
    .map((item) => ({
      projectId: item.projectId,
      ticker: item.ticker!,
      currentPrice: item.currentPrice || 0,
      rating: item.rating!,
    }));

  return { items };
};

export const useCollectionDashboardData = (projectId?: string): CollectionDashboardData => {
  const collectionMetadataIds = useCollectionKey(projectId, "metadataIds");
  const collectionName = useCollectionKey(projectId, "name");
  const ticker = useCollectionKey(projectId, "ticker");
  const exchange = useCollectionKey(projectId, "stockExchange");
  const dashboardMetrics = useDashboardMetrics(collectionMetadataIds);

  return useMemo(
    () => ({
      collectionId: projectId,
      collectionName,
      ticker,
      exchange,
      metadataIds: collectionMetadataIds,
      dashboardMetrics,
    }),
    [projectId, collectionName, ticker, exchange, collectionMetadataIds, dashboardMetrics]
  );
};
